import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { DEFAULT_TIMEZONE, WIDGET_DISPLAY_TIMEZONE_SOURCE } from '../../../constants/widget-constants';
import { PartnerDao } from '../../../db-models/widget-conf-dao';
import { TimezoneDbModel } from '../../../db-models/timezone.model';
import { UserState } from '../../../models/state.model';
import { TimezoneService } from '../../../services/timezone.service';
import { LoggerService } from '../../../services/logger.service';
import { TranslationPipe } from '../../pipes/translation.pipe';

const modules = [NgSelectModule, FormsModule, TranslateModule];
const pipes = [TranslationPipe];

@Component({
  selector: 'app-timezone-select',
  imports: [...modules, ...pipes],
  templateUrl: './timezone-select.component.html',
  styleUrl: './timezone-select.component.scss'
})
export class TimezoneSelectComponent implements OnInit {

  private timezoneService = inject(TimezoneService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);

  @Input() protected timezones = new Array<TimezoneDbModel>();
  @Input() userState: UserState;
  @Input() partner?: PartnerDao;

  @Output('onTimezoneChange') onTimezoneChangeEvent = new EventEmitter<string>();

  protected isTimezonesLoading = false;
  protected selectedTimezone: string;
  protected lang: string;

  ngOnInit(): void {
    this.lang = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);
    this.timezones?.length === 0 && this.getTimezones();
    this.preselectLocalTimezone();
  }

  private getTimezones() {
    this.isTimezonesLoading = true;
    this.timezoneService.getTimezones()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isTimezonesLoading = false)
      )
      .subscribe({
        next: timezones => this.timezones = timezones?.data || [],
        error: error => LoggerService.error(error),
      });
  }

  protected get usePartnerTimezone(): boolean {
    return this.partner?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER
      && !!this.partner?.timezone;
  }

  protected get partnerTimezoneLabel(): string {
    if (!this.usePartnerTimezone) return '';
    const found = this.timezones.find(tz => tz.zone === this.partner!.timezone);
    return found?.name ?? this.partner!.timezone!;
  }

  private preselectLocalTimezone(): void {
    // When partner has opted into 'partner' mode, force partner.timezone as the display TZ.
    // Otherwise preserve today's behaviour: existing pick → visitor browser TZ.
    if (this.usePartnerTimezone) {
      this.selectedTimezone = this.partner!.timezone!;
      this.userState.timezone = this.selectedTimezone;
      return;
    }
    const preferred = this.userState?.timezone || this.timezoneService.getLocalTimezone();
    const foundLocalTimezone = this.timezones.find(timezone => timezone.zone_group.includes(preferred));
    this.selectedTimezone = foundLocalTimezone ? foundLocalTimezone.zone : DEFAULT_TIMEZONE;
    this.userState.timezone = this.selectedTimezone;
  }

  protected onTimezoneChange(timezone: string) {
    this.onTimezoneChangeEvent.emit(timezone);
  }
}
